import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError, getTour } from "@/lib/api";
import type {
  DeclaredShowPeriodsResponse,
  FeedAuditEntry,
  FeedAuditResponse,
  FeedSuggestionsResponse,
  NECVerdict,
  SpatialGroup,
  SuggestionsGrouping,
  SuggestionsStrategy,
  Tier1Result,
  Tour,
  TourVocabularyEntry,
  Window,
} from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { NeedsTaggingSidebar } from "@/components/NeedsTaggingSidebar";
import { RosterPanel } from "@/components/RosterPanel";
import { Progress } from "@/components/ui/progress";
import { useAggressiveStream } from "@/hooks/useAggressiveStream";
import { VenuePlannerSection } from "@/components/VenuePlannerSection";
import { cn } from "@/lib/utils";

// Headroom thresholds mirror the server (KD4 / KD6).
const NEC_CONTINUOUS_PCT = 80;
const BREAKER_TRIP_PCT = 100;
const RECENT_EVENTS_LOOKBACK_DAYS = 90;

// Color tiers for headroom %.
function headroomTone(pct: number | null, threshold: number): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= threshold) return "text-red-400";
  if (pct >= threshold * 0.95) return "text-amber-400";
  return "text-emerald-400";
}

// Format helpers.
function fmtAmps(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}A`;
}
function fmtPct(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}%`;
}

// -----------------------------------------------------------------------------
// Top-level route: tour-scope picker + sub-view switcher (Audit | Suggestions)
// -----------------------------------------------------------------------------

export function FeedAudit() {
  const [sp, setSp] = useSearchParams();
  const windowId = sp.get("window_id") ?? sp.get("window") ?? "";
  const tourScopeRaw = sp.get("tour_scope") ?? "";
  const view = (sp.get("view") ?? "audit") as "audit" | "suggestions";

  const tourScope = useMemo(
    () => tourScopeRaw.split(",").map((s) => s.trim()).filter(Boolean),
    [tourScopeRaw],
  );

  // Resolved tour-scope picker state. Defaults to [windowId] when nothing is
  // selected (KD1: no prefix heuristic — operator must opt in to multi-event).
  const effectiveTourScope = tourScope.length > 0
    ? tourScope
    : (windowId ? [windowId] : []);

  function setView(v: "audit" | "suggestions") {
    const next = new URLSearchParams(sp);
    next.set("view", v);
    setSp(next, { replace: true });
  }

  function setTourScope(ids: string[]) {
    const next = new URLSearchParams(sp);
    if (ids.length === 0) {
      next.delete("tour_scope");
    } else {
      next.set("tour_scope", ids.join(","));
    }
    setSp(next, { replace: true });
  }

  if (!windowId) {
    return (
      <div className="max-w-3xl mx-auto py-12">
        <Card>
          <CardHeader>
            <CardTitle>House Feeds</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Pick an event first (use the window picker in the header) to view
              its house-feed audit, or use the Suggestions view to plan the next city.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h1 className="text-xl font-semibold">House Feeds</h1>
        <div className="ml-auto flex items-center gap-1">
          <Button
            variant={view === "audit" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("audit")}
          >
            Audit
          </Button>
          <Button
            variant={view === "suggestions" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setView("suggestions")}
          >
            Suggestions
          </Button>
        </div>
      </div>

      <TourScopePicker
        currentWindowId={windowId}
        selectedIds={effectiveTourScope}
        onChange={setTourScope}
      />

      {view === "audit"
        ? <AuditView windowId={windowId} />
        : <SuggestionsView windowId={windowId} tourScope={effectiveTourScope} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tour-scope picker — explicit multi-select (KD1). Per-event coincident peak
// shown inline beside each checkbox so the operator can spot small-market
// outliers at scope-selection time (round-2 finding F3 mitigation).
// -----------------------------------------------------------------------------

function TourScopePicker({
  currentWindowId,
  selectedIds,
  onChange,
}: {
  currentWindowId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [windows, setWindows] = useState<Window[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    const cutoffMs = Date.now() - RECENT_EVENTS_LOOKBACK_DAYS * 24 * 3600 * 1000;
    api.get<{ windows: Window[] }>("/api/windows?include_ended=true")
      .then((d) => {
        const recent = d.windows.filter((w) => {
          const t = Date.parse(w.started_at || "");
          return Number.isFinite(t) && t >= cutoffMs;
        });
        recent.sort((a, b) =>
          (b.started_at || "").localeCompare(a.started_at || "")
        );
        setWindows(recent);
      })
      .catch((e: Error) => {
        setError(e.message);
        setWindows([]);
      })
      .finally(() => setLoading(false));
  }, [open]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  function toggle(id: string) {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Always keep currentWindowId checked.
    next.add(currentWindowId);
    onChange(Array.from(next));
  }

  function clearAndJustCurrent() {
    onChange([currentWindowId]);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 space-y-0 pb-3">
        <CardTitle className="text-sm font-medium">
          Tour scope ({selectedIds.length} event{selectedIds.length === 1 ? "" : "s"})
        </CardTitle>
        <div className="ml-auto flex items-center gap-1">
          {selectedIds.length > 1 && (
            <Button variant="ghost" size="sm" onClick={clearAndJustCurrent}>
              Just this event
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
            {open ? "Hide" : "Edit scope"}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          {loading && <div className="text-sm text-muted-foreground">Loading recent events…</div>}
          {error && <div className="text-sm text-red-400">{error}</div>}
          {!loading && !error && (
            <div className="max-h-72 overflow-y-auto divide-y divide-border/40">
              {windows.map((w) => {
                const isCurrent = w.window_id === currentWindowId;
                const checked = selectedSet.has(w.window_id);
                return (
                  <label
                    key={w.window_id}
                    className={cn(
                      "flex items-center gap-3 py-2 px-1 cursor-pointer text-sm",
                      isCurrent && "opacity-90",
                    )}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isCurrent}
                      onChange={() => toggle(w.window_id)}
                    />
                    <span className="font-medium">{w.name}</span>
                    {isCurrent && (
                      <span className="text-xs text-muted-foreground">(current)</span>
                    )}
                    <span className="ml-auto text-xs text-muted-foreground">
                      {(w.started_at || "").slice(0, 10)}
                    </span>
                  </label>
                );
              })}
              {windows.length === 0 && (
                <div className="text-sm text-muted-foreground py-2">
                  No recent events in the last {RECENT_EVENTS_LOOKBACK_DAYS} days.
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// Audit view
// -----------------------------------------------------------------------------

function AuditView({ windowId }: { windowId: string }) {
  const [data, setData] = useState<FeedAuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    api.get<FeedAuditResponse>(
      `/api/feed-audit?window_id=${encodeURIComponent(windowId)}`,
      { signal: ctrl.signal },
    )
      .then((d) => setData(d))
      .catch((e: Error) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setData(null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [windowId]);

  useEffect(() => {
    reload();
    return () => abortRef.current?.abort();
  }, [reload]);

  if (loading && !data) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="opacity-50 animate-pulse">
            <CardContent className="h-32" />
          </Card>
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <Card>
        <CardContent className="py-6 flex items-center gap-4">
          <p className="text-sm text-red-400">
            Could not reach dyno data — {error}
          </p>
          <Button size="sm" variant="outline" onClick={reload}>Retry</Button>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  if (data.feeds.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <p className="text-sm">
            No memberships in this event have been tagged with a house feed yet.
          </p>
          {data.untagged_services.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {data.untagged_services.length} untagged service
              {data.untagged_services.length === 1 ? "" : "s"} waiting to be assigned —{" "}
              <a className="underline" href={`/admin/load-in?window=${encodeURIComponent(windowId)}`}>
                tag in Load-in
              </a>
              .
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <ServiceAuditExportBanner windowId={data.window_id} />
      <Caveat />
      {data.feeds.map((feed) => (
        <FeedAuditCard key={feed.feed_id} feed={feed} windowId={data.window_id} />
      ))}
      {data.untagged_services.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">
              Untagged services ({data.untagged_services.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-muted-foreground space-y-1">
            {data.untagged_services.map((s) => (
              <div key={s.system_id}>
                <span className="font-mono">{s.system_id}</span>
                {s.service_name && <span> · {s.service_name}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FeedAuditCard({ feed, windowId }: { feed: FeedAuditEntry; windowId: string }) {
  const transientPct = feed.transient_headroom_pct_at_100;
  const continuousPct = feed.continuous_headroom_pct_at_80;
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-base">{feed.feed_name}</CardTitle>
          <span className="text-xs text-muted-foreground">
            ({feed.capacity_amps}A rated)
          </span>
          {feed.location_label && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
              {feed.location_label}
            </span>
          )}
          <DivergenceBadge feed={feed} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <Metric
            label="Transient peak"
            sublabel="vs 100% breaker"
            valueA={feed.worst_leg_transient_a}
            pct={transientPct}
            tone={headroomTone(transientPct, BREAKER_TRIP_PCT)}
          />
          <Metric
            label="Continuous peak"
            sublabel={
              feed.worst_leg_continuous_a == null
                ? "no ≥3h sustained run"
                : "vs 80% NEC continuous"
            }
            valueA={feed.worst_leg_continuous_a}
            pct={continuousPct}
            tone={headroomTone(continuousPct, NEC_CONTINUOUS_PCT)}
          />
          <Metric
            label="Sum-of-peaks"
            sublabel="conservative sizing"
            valueA={feed.sum_of_peaks_a}
            pct={null}
            tone="text-muted-foreground"
          />
          <Metric
            label="Diversity factor"
            sublabel="coincident ÷ sum"
            valueA={null}
            pct={null}
            tone="text-muted-foreground"
            valueOverride={
              feed.diversity_factor == null
                ? "—"
                : feed.diversity_factor.toFixed(2)
            }
          />
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Per-leg breakdown
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            {(["l1", "l2", "l3"] as const).map((leg) => {
              const trans = feed.leg_transient[leg];
              const cont = feed.leg_continuous_a[leg];
              const transPct = feed.capacity_amps > 0
                ? (trans / feed.capacity_amps) * 100
                : 0;
              const contPct = cont != null && feed.capacity_amps > 0
                ? (cont / (feed.capacity_amps * (NEC_CONTINUOUS_PCT / 100))) * 100
                : null;
              return (
                <div key={leg} className="rounded border border-border/60 p-2">
                  <div className="text-xs font-mono text-muted-foreground">
                    Leg {leg.toUpperCase()}
                  </div>
                  <div className={cn("text-sm", headroomTone(transPct, BREAKER_TRIP_PCT))}>
                    {fmtAmps(trans)} transient ({fmtPct(transPct)})
                  </div>
                  <div className={cn("text-sm", headroomTone(contPct, NEC_CONTINUOUS_PCT))}>
                    {cont == null
                      ? "no sustained run"
                      : `${fmtAmps(cont)} continuous (${fmtPct(contPct)})`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <CableRecommendationBlock rec={feed.sc_cable_recommendation} />

        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Services on this feed
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>System</TableHead>
                <TableHead className="text-right">Worst-leg peak</TableHead>
                <TableHead className="text-right w-24">Audit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {feed.services.map((s) => (
                <TableRow key={s.system_id}>
                  <TableCell className="font-mono text-xs">{s.system_id}</TableCell>
                  <TableCell className="text-right">{fmtAmps(s.peak_a)}</TableCell>
                  <TableCell className="text-right">
                    <Link
                      to={`/admin/services/${encodeURIComponent(s.system_id)}/audit?window_id=${encodeURIComponent(windowId)}`}
                      className="text-xs underline text-muted-foreground hover:text-foreground"
                    >
                      open →
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function Metric({
  label, sublabel, valueA, pct, tone, valueOverride,
}: {
  label: string;
  sublabel: string;
  valueA: number | null;
  pct: number | null;
  tone: string;
  valueOverride?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-lg font-semibold", tone)}>
        {valueOverride ?? (valueA == null ? "—" : fmtAmps(valueA))}
        {pct != null && (
          <span className="text-xs ml-2 align-middle text-muted-foreground">
            {fmtPct(pct)}
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground">{sublabel}</div>
    </div>
  );
}

interface ReportBuildResult {
  success?: boolean;
  service_count?: number;
  venue_count?: number;
  report_dir?: string;
  pdf_ok?: boolean;
  html_ok?: boolean;
  pdf_url?: string | null;
  html_url?: string | null;
  json_url?: string | null;
  error?: string;
}

function ReportBuildRow({
  title, subtitle, endpoint, countLabel,
}: {
  title: string;
  subtitle: string;
  endpoint: string;
  countLabel: (r: ReportBuildResult) => string;
}) {
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<ReportBuildResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async () => {
    setBusy(true);
    setLast(null);
    setError(null);
    try {
      const resp = await api.post<ReportBuildResult>(endpoint, {});
      if (resp?.success) {
        setLast(resp);
      } else {
        setError(resp?.error ?? "unknown error");
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [endpoint]);

  return (
    <div className="flex items-center gap-3 flex-wrap py-2">
      <div className="text-sm">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-muted-foreground">{subtitle}</div>
      </div>
      <Button
        size="sm" variant="default" onClick={run} disabled={busy}
        className="ml-auto"
      >
        {busy ? "Building…" : "Build report"}
      </Button>
      {last && (
        <div className="basis-full flex items-center gap-3 flex-wrap text-xs">
          <span className="text-emerald-300">Built — {countLabel(last)}</span>
          {last.pdf_url && (
            <>
              <a href={last.pdf_url} target="_blank" rel="noopener"
                className="underline text-foreground">Open PDF</a>
              <a href={`${last.pdf_url}?download=1`}
                className="underline text-muted-foreground">Download</a>
            </>
          )}
          {last.html_url && (
            <a href={last.html_url} target="_blank" rel="noopener"
              className="underline text-muted-foreground">View HTML</a>
          )}
          {!last.pdf_url && (
            <span className="text-amber-300">
              PDF unavailable (reports service may be offline)
            </span>
          )}
        </div>
      )}
      {error && (
        <div className="basis-full text-xs text-red-400">Failed: {error}</div>
      )}
    </div>
  );
}

function ServiceAuditExportBanner({ windowId }: { windowId: string }) {
  const [tourId, setTourId] = useState<string | null>(null);

  useEffect(() => {
    if (!windowId) return;
    api.get<Window>(`/api/windows/${encodeURIComponent(windowId)}`)
      .then((w) => setTourId(w.tour_id ?? null))
      .catch(() => setTourId(null));
  }, [windowId]);

  return (
    <Card>
      <CardContent className="py-1 divide-y divide-border/40">
        <ReportBuildRow
          title="Service audit (this event)"
          subtitle="Batched PDF — one row per service with sizing core + recommended cable."
          endpoint={`/api/windows/${encodeURIComponent(windowId)}/service-audit-report`}
          countLabel={(r) => `${r.service_count ?? "?"} services`}
        />
        {tourId && (
          <ReportBuildRow
            title="Tour feed plan — per venue"
            subtitle="Per-venue deployment cards: which feed each touring identity lands on and the cable spec, sized to this venue's worst case across past stops."
            endpoint={`/api/tours/${encodeURIComponent(tourId)}/venue-plan-report`}
            countLabel={(r) => `${r.venue_count ?? "?"} venues`}
          />
        )}
      </CardContent>
    </Card>
  );
}

function CableRecommendationBlock({
  rec,
}: {
  rec: FeedAuditEntry["sc_cable_recommendation"];
}) {
  if (!rec) return null;
  const sizeLabel = rec.parallel > 1
    ? `${rec.parallel}× 4/0 parallel`
    : `${rec.awg} AWG`;
  const tone = rec.limiter === "over_max"
    ? "text-amber-300"
    : rec.awg === "4/0" || rec.awg === "2/0"
      ? "text-muted-foreground"
      : "text-emerald-300";
  return (
    <div className="rounded border border-border/60 p-3 bg-secondary/20">
      <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
        Recommended cable (Type SC, NEC 400-6(B))
      </div>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className={cn("text-lg font-semibold", tone)}>{sizeLabel}</span>
        <span className="text-xs text-muted-foreground">
          {rec.parallel * rec.ampacity_a} A ampacity · needs ≥{rec.needed_a.toFixed(0)} A
          ({rec.limiter} gate)
        </span>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Smallest camlock-standard size that satisfies the observed peak + NEC 80% continuous derate.
      </div>
    </div>
  );
}

function DivergenceBadge({ feed }: { feed: FeedAuditEntry }) {
  if (feed.catalog_current_state === "present") return null;
  const text =
    feed.catalog_current_state === "renamed"
      ? `renamed → ${feed.catalog_feed_name}`
      : feed.catalog_current_state === "rerated"
        ? `rerated → ${feed.catalog_capacity_amps}A`
        : "removed from catalog";
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300"
      title="This audit reflects the feed configuration at the time of the show; the catalog has since been edited."
    >
      {text}
    </span>
  );
}

function Caveat() {
  return (
    <p className="text-xs italic text-amber-300/80 px-1">
      <span className="text-amber-400">*</span> Coincident values computed from
      this tour's observed load profile. Future shows with similar production
      are likely to behave similarly but not guaranteed; verify against rider
      before committing distribution.
    </p>
  );
}

// -----------------------------------------------------------------------------
// Suggestions view
// -----------------------------------------------------------------------------

function SuggestionsView({
  windowId, tourScope,
}: { windowId: string; tourScope: string[] }) {
  const [data, setData] = useState<FeedSuggestionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aggressiveAck, setAggressiveAck] = useState(false);
  const [venueId, setVenueId] = useState<string | null>(null);
  // Roster spatial-first tours (U10 scaffolding): the current event's tour_id
  // drives the NeedsTaggingSidebar's vocab dropdown. Null on one-off events;
  // sidebar falls back to a free-text service_location_label editor.
  const [tourId, setTourId] = useState<string | null>(null);
  const [tour, setTour] = useState<Tour | null>(null);
  // Whether the current event has at least one declared show window — drives
  // the roster panel's R9 pending-copy split ("no window" vs "not occurred").
  const [hasDeclaredShow, setHasDeclaredShow] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Resolve the current event's venue_id + tour_id once.
  useEffect(() => {
    api.get<Window>(`/api/windows/${encodeURIComponent(windowId)}`)
      .then((w) => {
        setVenueId(w.venue_id ?? null);
        setTourId(w.tour_id ?? null);
      })
      .catch(() => {
        setVenueId(null);
        setTourId(null);
      });
  }, [windowId]);

  // Tour vocabulary fetch — only when the event is linked to a tour. Cached
  // locally; refreshed via reloadTour() when the sidebar's "+ Add new
  // identity" succeeds so the new entry shows up in the dropdown.
  useEffect(() => {
    if (!tourId) {
      setTour(null);
      return;
    }
    let cancelled = false;
    getTour(tourId)
      .then((t) => { if (!cancelled) setTour(t); })
      .catch(() => { if (!cancelled) setTour(null); });
    return () => { cancelled = true; };
  }, [tourId]);

  const reloadTour = useCallback(async () => {
    if (!tourId) return;
    try {
      const fresh = await getTour(tourId);
      setTour(fresh);
    } catch {
      // Best-effort refresh; the next interaction will reconcile.
    }
  }, [tourId]);

  // Declared-show presence — admin-only endpoint; a 403/error degrades to
  // "no declared show" (the panel still renders, just with the other copy).
  useEffect(() => {
    setHasDeclaredShow(false);
    api.get<DeclaredShowPeriodsResponse>(
      `/api/windows/${encodeURIComponent(windowId)}/show-periods`,
      { skipAuthRedirect: true },
    )
      .then((d) => setHasDeclaredShow((d.periods?.length ?? 0) > 0))
      .catch(() => setHasDeclaredShow(false));
  }, [windowId]);

  const reload = useCallback(() => {
    if (!venueId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      tour_scope: tourScope.join(","),
      venue_id: venueId,
      aggressive_acknowledged: aggressiveAck ? "true" : "false",
    });
    api.get<FeedSuggestionsResponse>(
      `/api/feed-suggestions?${params.toString()}`,
      { signal: ctrl.signal },
    )
      .then(setData)
      .catch((e: ApiError | Error) => {
        if (e.name === "AbortError") return;
        setError(e.message);
        setData(null);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [venueId, tourScope, aggressiveAck]);

  useEffect(() => {
    reload();
    return () => abortRef.current?.abort();
  }, [reload]);

  if (!venueId) {
    return (
      <Card>
        <CardContent className="py-6">
          <p className="text-sm text-muted-foreground">
            This event has no venue assigned. Set a venue on the event to compute
            suggestions against the venue's house-feed catalog.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <AggressiveToggle ack={aggressiveAck} onChange={setAggressiveAck} />
      <Caveat />
      {loading && !data && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="opacity-50 animate-pulse">
              <CardContent className="h-40" />
            </Card>
          ))}
        </div>
      )}
      {error && (
        <Card>
          <CardContent className="py-6 flex items-center gap-4">
            <p className="text-sm text-red-400">{error}</p>
            <Button size="sm" variant="outline" onClick={reload}>Retry</Button>
          </CardContent>
        </Card>
      )}
      {/* Venue-aware load-in planner (post-roster-infra). Lives above the
          legacy abstract-amperage enumeration so operators can compare; the
          older surface will be retired once this v1 is validated against
          real planning. */}
      {data && data.services_input.length > 0 && (
        <VenuePlannerSection
          windowId={windowId}
          sourceVenueId={data.venue_id || null}
          services={data.services_input}
        />
      )}
      {data && (() => {
        // U10 PARTIAL: render the sidebar + 2-column flex when the backend
        // returns a populated `untagged_services` field. Until U7 ships this
        // field is absent (or empty), and the strategy cards take the full
        // width — preserving today's behavior.
        const untagged = data.untagged_services ?? [];
        const showSidebar = untagged.length > 0;
        // Map system_id → human-readable service name so candidate
        // rows render "House Audio" not "acuvim-93a4". Falls back to
        // system_id when name is null.
        const serviceNameById: Record<string, string> = {};
        for (const svc of data.services_input) {
          serviceNameById[svc.system_id] = svc.service_name || svc.system_id;
        }
        const cardsGrid = (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
            <StrategyCard
              label="Balanced"
              description="NEC 210.20 continuous (80%)"
              strategy={data.strategies.balanced}
              serviceNameById={serviceNameById}
              primary
            />
            <StrategyCard
              label="Safest"
              description="Max headroom (70%)"
              strategy={data.strategies.safest}
              serviceNameById={serviceNameById}
            />
            {data.strategies.aggressive &&
              (data.strategies.aggressive.available === false ? (
                <AggressiveUnavailableCard
                  reason={(data.strategies.aggressive as { available: false; reason: string }).reason}
                />
              ) : (
                <StrategyCard
                  label="Aggressive"
                  description="Non-continuous only (90%)"
                  strategy={data.strategies.aggressive}
                  serviceNameById={serviceNameById}
                  aggressive
                />
              ))}
          </div>
        );
        if (!showSidebar) return cardsGrid;
        const vocab: TourVocabularyEntry[] =
          tour?.touring_service_vocabulary ?? [];
        return (
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="w-full lg:w-[280px] lg:flex-shrink-0">
              <NeedsTaggingSidebar
                untaggedServices={untagged}
                tourId={tourId}
                tourVocabulary={vocab}
                onTagged={reload}
                onVocabRefresh={reloadTour}
              />
            </div>
            <div className="flex-1 min-w-0 w-full">{cardsGrid}</div>
          </div>
        );
      })()}
      {data && data.services_input.length > 0 && (
        <ServicesInputCard data={data} />
      )}
      {/* Roster panel — the admin-only planning surface (U9). Mounted under
          the strategy cards: hides itself on a non-admin host (403), and is
          keyed on the current event, not the tour scope. */}
      <RosterPanel
        windowId={windowId}
        hasDeclaredShow={hasDeclaredShow}
        safestLowerBound={data?.safest_lower_bound ?? null}
      />
    </div>
  );
}

function AggressiveToggle({
  ack, onChange,
}: { ack: boolean; onChange: (v: boolean) => void }) {
  return (
    <Card>
      <CardContent className="py-3 flex items-start gap-3">
        <input
          type="checkbox"
          id="aggressive-ack"
          checked={ack}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1"
        />
        <Label htmlFor="aggressive-ack" className="font-normal text-sm cursor-pointer">
          Show "Aggressive" strategy (non-continuous loads only)
          <div className="text-xs text-muted-foreground mt-0.5">
            Server will refuse any grouping whose per-leg load sustains ≥85% of
            the proposed rating for ≥3h in tour data. Per NEC 210.20.
          </div>
        </Label>
      </CardContent>
    </Card>
  );
}

function AggressiveUnavailableCard({ reason }: { reason: string }) {
  // The backend returns {available:false, reason} for Aggressive when no
  // occurred declared show exists in tour scope — the coincidence envelope
  // is empty, so the continuity gate has nothing to evaluate (AE3).
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">Aggressive</CardTitle>
          <span className="text-xs text-muted-foreground">
            Non-continuous only (90%)
          </span>
        </div>
        <div className="text-xs text-amber-400">Unavailable</div>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{reason}</p>
      </CardContent>
    </Card>
  );
}

function StrategyCard({
  label, description, strategy, serviceNameById, primary, aggressive,
}: {
  label: string;
  description: string;
  strategy: SuggestionsStrategy;
  serviceNameById: Record<string, string>;
  primary?: boolean;
  aggressive?: boolean;
}) {
  // U10: Aggressive streaming consumer. When the backend dispatches a
  // streaming job (signaled by strategy.job_id), poll for progress and show
  // a progress bar header until status === "complete". Per-candidate
  // verdict overlay (Skeleton placeholders → real verdicts) is a follow-up.
  const stream = useAggressiveStream(
    aggressive ? (strategy.job_id ?? null) : null,
  );
  return (
    <Card className={cn(primary && "border-emerald-500/60")}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <CardTitle className="text-base">{label}</CardTitle>
          <span className="text-xs text-muted-foreground">{description}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {strategy.total_feeds} feed{strategy.total_feeds === 1 ? "" : "s"}
          {strategy.diversity_factor != null && (
            <span> · diversity {strategy.diversity_factor.toFixed(2)}</span>
          )}
        </div>
        {aggressive && stream.status === "computing" && stream.total > 0 && (
          <div className="mt-2 space-y-1">
            <Progress value={(stream.completed / stream.total) * 100} />
            <div className="text-xs text-muted-foreground">
              Computing {stream.completed} of {stream.total} candidate
              {stream.total === 1 ? "" : "s"}…
            </div>
          </div>
        )}
        {aggressive && stream.status === "expired" && (
          <div className="mt-2 text-xs text-amber-400">
            {stream.errorMessage} Reload the page to recompute.
          </div>
        )}
        {aggressive && stream.status === "error" && (
          <div className="mt-2 text-xs text-red-400">
            Aggressive polling failed: {stream.errorMessage}
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {strategy.spatial_groups && strategy.spatial_groups.length > 0 && (
          <RecommendedPlanSection
            spatialGroups={strategy.spatial_groups}
            streamResults={aggressive ? stream.results : null}
            serviceNameById={serviceNameById}
          />
        )}
        {strategy.spatial_groups && strategy.spatial_groups.length > 0 ? (
          <details className="rounded border border-border/40">
            <summary className="cursor-pointer text-xs text-muted-foreground px-2 py-1 hover:bg-muted/30">
              Show all candidates
              {strategy.summary && (
                <span className="ml-1">
                  ({strategy.summary.fitting_candidates} of {strategy.summary.total_candidates} fit)
                </span>
              )}
            </summary>
            <div className="space-y-3 p-2">
              {strategy.spatial_groups.map((sg, sgIdx) => (
                <SpatialGroupSection
                  key={sgIdx}
                  group={sg}
                  streamResults={aggressive ? stream.results : null}
                  serviceNameById={serviceNameById}
                />
              ))}
            </div>
          </details>
        ) : (
          <>
            {(strategy.groupings ?? []).map((g, idx) => (
              <GroupingRow
                key={idx}
                grouping={g}
                streamResults={aggressive ? stream.results : null}
                serviceNameById={serviceNameById}
              />
            ))}
            {(strategy.groupings ?? []).length === 0 && (
              <p className="text-xs text-muted-foreground">No groupings produced.</p>
            )}
          </>
        )}
        {(strategy.failed?.length ?? 0) > 0 && (
          <div className="text-xs">
            <div className="text-amber-400 mb-1">
              Could not place ({strategy.failed!.length}):
            </div>
            <ul className="text-muted-foreground space-y-0.5">
              {strategy.failed!.map((f, i) => (
                <li key={i}>
                  <span className="font-mono">{f.service.system_id}</span> — {f.reason}
                </li>
              ))}
            </ul>
          </div>
        )}
        {aggressive && strategy.rejected && strategy.rejected.length > 0 && (
          <div className="text-xs border-t border-border/40 pt-3">
            <div className="text-red-400 mb-1">
              Rejected by NEC-continuous check ({strategy.rejected.length}):
            </div>
            <ul className="text-muted-foreground space-y-1">
              {strategy.rejected.map((r, i) => (
                <li key={i}>
                  <div>
                    {r.grouping.feed_rating_a}A feed:
                    {" "}
                    {r.grouping.services.map((s) => s.system_id).join(", ")}
                  </div>
                  <div className="text-amber-300/80">{r.reason}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// U10 PARTIAL: spatial-group subheading + nested groupings. Only rendered
// when the backend returns `spatial_groups` (U7 forward-compat). Each group
// represents one (tier, touring_identity) partition.
// Pick the recommended grouping per spatial partition.
// Priority: (1) must fit (verdict.fits === true, falling back to streamed
// verdict for Aggressive's null-initially case); (2) most members covered
// (max single-feed coverage); (3) tie-break by LARGEST proposed_rating_a
// per Sean's "don't prioritize smallest feeds — extra overhead is fine."
function pickRecommendation(
  groupings: SuggestionsGrouping[],
  streamResults: Record<string, Tier1Result | null> | null,
): { grouping: SuggestionsGrouping; verdict: NECVerdict } | null {
  type Scored = { g: SuggestionsGrouping; v: NECVerdict };
  const fitting: Scored[] = [];
  for (const g of groupings) {
    const own = g.verdict;
    const streamed = streamResults?.[g.candidate_key]?.verdict ?? null;
    const v = own ?? streamed;
    if (v && v.fits) fitting.push({ g, v });
  }
  if (fitting.length === 0) return null;
  fitting.sort((a, b) => {
    const am = (a.g.member_ids ?? []).length;
    const bm = (b.g.member_ids ?? []).length;
    if (am !== bm) return bm - am;
    return (b.g.proposed_rating_a ?? 0) - (a.g.proposed_rating_a ?? 0);
  });
  return { grouping: fitting[0].g, verdict: fitting[0].v };
}

function RecommendedPlanSection({
  spatialGroups, streamResults, serviceNameById,
}: {
  spatialGroups: SpatialGroup[];
  streamResults: Record<string, Tier1Result | null> | null;
  serviceNameById: Record<string, string>;
}) {
  // Per partition, compute the recommended grouping + uncovered members
  // (services in the partition the recommendation does NOT include).
  const plan = spatialGroups.map((sg) => {
    const partitionMemberIds = new Set<string>();
    for (const g of sg.groupings) {
      for (const mid of g.member_ids ?? []) partitionMemberIds.add(mid);
    }
    const rec = pickRecommendation(sg.groupings, streamResults);
    const covered = new Set(rec?.grouping.member_ids ?? []);
    const uncovered = [...partitionMemberIds].filter((m) => !covered.has(m));
    return { partition: sg, rec, uncovered };
  });
  return (
    <div className="space-y-2 rounded border border-emerald-500/40 bg-emerald-500/5 p-3">
      <div className="text-xs uppercase tracking-wide text-emerald-300">
        Recommended plan
      </div>
      {plan.map(({ partition, rec, uncovered }, idx) => (
        <div key={idx} className="text-sm space-y-1">
          <div className="text-xs text-muted-foreground">
            {partition.touring_identity} · {partition.tier}
          </div>
          {rec ? (
            <div className="pl-2">
              <div>
                <span className="font-medium">{rec.grouping.proposed_rating_a}A feed</span>
                <span className="text-xs text-muted-foreground ml-2">
                  peak {Math.round(rec.verdict.transient_pct * 100)}% / continuous {Math.round(rec.verdict.continuous_pct * 100)}%
                </span>
              </div>
              <ul className="text-xs text-muted-foreground pl-3 list-disc">
                {(rec.grouping.member_ids ?? []).map((mid) => (
                  <li key={mid}>{serviceNameById[mid] ?? mid}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="pl-2 text-amber-400 text-xs">
              ⚠ no single grouping fits — see all candidates below
            </div>
          )}
          {uncovered.length > 0 && (
            <div className="pl-2 text-xs text-amber-400">
              Also needs its own feed:{" "}
              {uncovered.map((m) => serviceNameById[m] ?? m).join(", ")}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SpatialGroupSection({
  group, streamResults, serviceNameById,
}: {
  group: SpatialGroup;
  streamResults: Record<string, Tier1Result | null> | null;
  serviceNameById: Record<string, string>;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border/40 pb-1">
        {group.touring_identity} · {group.tier}
      </div>
      {group.groupings.length === 0
        ? (
          <p className="text-xs text-muted-foreground italic">
            No groupings produced.
          </p>
        )
        : group.groupings.map((g, idx) => (
          <GroupingRow
            key={idx}
            grouping={g}
            streamResults={streamResults}
            serviceNameById={serviceNameById}
          />
        ))}
    </div>
  );
}

function GroupingRow({
  grouping, streamResults, serviceNameById,
}: {
  grouping: SuggestionsGrouping;
  streamResults: Record<string, Tier1Result | null> | null;
  serviceNameById: Record<string, string>;
}) {
  // U7 PassB canonical shape: candidate_key / member_ids / proposed_rating_a /
  // sum_show_condition_max_a / verdict. Pre-U7 fields (services/feed_rating_a/
  // coincident_peak_a) are no longer returned by the backend but the type
  // keeps them optional for legacy consumers — use the new fields with
  // fallback so we don't crash on either shape.
  const ratingA = grouping.proposed_rating_a ?? grouping.feed_rating_a ?? 0;
  const sumA = grouping.sum_show_condition_max_a ?? grouping.coincident_peak_a ?? 0;
  const memberIds = grouping.member_ids
    ?? grouping.services?.map((s) => s.system_id)
    ?? [];
  // Aggressive streaming overlay: when the initial response carries
  // verdict=null (the orchestrator dispatched the candidate into the
  // background job), look up the Tier 1 result by candidate_key in the
  // polled stream. Falls back to grouping.verdict for Safest + Balanced,
  // which arrive synchronously.
  const streamed = streamResults?.[grouping.candidate_key] ?? null;
  const verdict = grouping.verdict ?? streamed?.verdict ?? null;
  const verdictTone = verdict
    ? verdict.fits ? "text-emerald-400"
    : verdict.fits_transient ? "text-amber-400"
    : "text-red-400"
    : "text-muted-foreground";
  // verdict.transient_pct / continuous_pct are RATIOS in [0,1]; render as
  // percent. The legacy fmtPct(...) helper expects already-0-100, so format
  // inline here instead of changing fmtPct's contract for the other callers.
  const peakPct = verdict ? Math.round(verdict.transient_pct * 100) : null;
  const contPct = verdict ? Math.round(verdict.continuous_pct * 100) : null;
  const badge = verdict
    ? verdict.fits
      ? `✓ fits — peak ${peakPct}% / continuous ${contPct}% of ${ratingA}A`
    : verdict.fits_transient
      ? `⚠ continuous over — peak ${peakPct}% OK but 3h average ${contPct}% trips`
      : `✗ peak over — ${peakPct}% of ${ratingA}A`
    : null;
  const cable = grouping.sc_cable_recommendation;
  const cableLabel = cable
    ? (cable.parallel > 1 ? `${cable.parallel}× 4/0` : `${cable.awg} AWG`)
    : null;
  const cableTone = !cable
    ? "text-muted-foreground"
    : cable.limiter === "over_max"
      ? "text-amber-300"
      : cable.awg === "4/0" || cable.awg === "2/0"
        ? "text-muted-foreground"
        : "text-emerald-300";
  return (
    <div className="rounded border border-border/60 p-2 space-y-1 text-sm">
      <div className="flex items-center gap-2">
        <span className="font-medium">{ratingA}A feed</span>
        <span className="text-xs text-muted-foreground">
          sum {fmtAmps(sumA)}
        </span>
        {cableLabel && (
          <span
            className={cn("text-xs px-1.5 py-0.5 rounded border border-border/60", cableTone)}
            title={cable!.basis}
          >
            cable: {cableLabel}
          </span>
        )}
        {badge ? (
          <span className={cn("ml-auto text-xs", verdictTone)}>{badge}</span>
        ) : (
          <span className="ml-auto text-xs text-muted-foreground italic">
            computing…
          </span>
        )}
      </div>
      <ul className="text-xs text-muted-foreground space-y-0.5">
        {memberIds.map((mid) => (
          <li key={mid}>{serviceNameById[mid] ?? mid}</li>
        ))}
      </ul>
    </div>
  );
}

// Roster data_state → human label + tone. Mirrors the U8 service_load_roster
// data_state values surfaced on each planning-input row.
const ROSTER_STATE_LABEL: Record<string, string> = {
  ok: "OK",
  pending: "Pending show",
  low_confidence: "Low confidence",
  moderate_confidence: "Moderate confidence",
  no_data: "No data",
  missing: "No roster row",
};

function rosterStateTone(state: string): string {
  if (state === "ok") return "text-muted-foreground";
  if (state === "moderate_confidence") return "text-yellow-400";
  if (state === "low_confidence" || state === "pending") return "text-amber-400";
  return "text-red-400"; // no_data | missing | anything unexpected
}

function ServicesInputCard({ data }: { data: FeedSuggestionsResponse }) {
  // Show per-service worst peak + the roster provenance state so operators can
  // see which services have solid telemetry behind their planning peak vs.
  // which are pending an occurred show or have no attributable data (U8).
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">
          Planning inputs ({data.services_input.length})
        </CardTitle>
        <div className="text-xs text-muted-foreground">
          Per-service worst-leg planning peak from the roster. The roster state
          tells you how trustworthy each peak is — "no data" / "pending" rows
          should be treated with caution.
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>System</TableHead>
              <TableHead>Location</TableHead>
              <TableHead className="text-right">Worst peak</TableHead>
              <TableHead className="text-right">Roster state</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.services_input.map((s) => (
              <TableRow key={s.system_id}>
                <TableCell className="font-mono text-xs">{s.system_id}</TableCell>
                <TableCell className="text-xs">{s.service_location_label ?? "—"}</TableCell>
                <TableCell className="text-right text-sm">
                  {fmtAmps(s.worst_coincident_peak_a)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-right text-xs",
                    rosterStateTone(s.roster_state),
                  )}
                >
                  {ROSTER_STATE_LABEL[s.roster_state] ?? s.roster_state}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
